import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Clock3,
  CreditCard,
  Loader2,
  LockKeyhole,
  RefreshCcw,
  ShieldCheck,
  Users,
} from 'lucide-react';
import {
  applyForfeoReserveUserCommand,
  createForfeoReserveFinancialCheckout,
  createForfeoReserveUserCatalogRequest,
  createForfeoReserveUserCommand,
  createForfeoReserveUserResolveRequest,
  currentForfeoReserveUserLocale,
  fetchForfeoReserveUserCatalog,
  resolveForfeoReserveUserSelection,
} from '../../lib/forfeoReserveUserExperienceClient';
import type {
  ForfeoReserveUserCatalogItem,
  ForfeoReserveUserLocale,
  ForfeoReserveUserOptionSelection,
  ForfeoReserveUserResolution,
} from '../../../supabase/functions/_shared/forfeo-reserve-user-experience';

type ForfeoReserveCatalogItemWithSource = ForfeoReserveUserCatalogItem & {
  sourceAccommodationId?: string;
};

type Props = {
  businessId: string;
  preferredAccommodationId?: string;
  initialPartySize?: number;
  initialDate?: string;
  initialEndDate?: string;
};
type Step = 'service' | 'schedule' | 'options' | 'review' | 'complete';

type Copy = {
  title: string;
  subtitle: string;
  service: string;
  schedule: string;
  options: string;
  review: string;
  complete: string;
  date: string;
  time: string;
  duration: string;
  people: string;
  noOptions: string;
  availability: string;
  policy: string;
  next: string;
  back: string;
  check: string;
  reserve: string;
  pay: string;
  waitlist: string;
  remaining: string;
  expires: string;
  confirmed: string;
  paymentRedirect: string;
  payOnSite: string;
  giftshopAuthority: string;
  retry: string;
};

const COPY: Record<ForfeoReserveUserLocale, Copy> = {
  'fr-CA': {
    title: 'Réserver avec Forfeo Reserve',
    subtitle: 'Choisissez un créneau réel. Les paiements et pass officiels demeurent gérés par Forfeo.com.',
    service: 'Service', schedule: 'Date et heure', options: 'Options', review: 'Vérification', complete: 'Confirmation',
    date: 'Date', time: 'Heure', duration: 'Durée', people: 'Personnes', noOptions: 'Aucune option supplémentaire pour ce service.',
    availability: 'Disponibilité', policy: 'Politique', next: 'Continuer', back: 'Retour', check: 'Vérifier le créneau',
    reserve: 'Confirmer la réservation', pay: 'Continuer vers Forfeo.com', waitlist: 'Joindre la liste d’attente',
    remaining: 'places restantes', expires: 'Le créneau est retenu temporairement.', confirmed: 'Votre réservation est confirmée.',
    paymentRedirect: 'Redirection vers le paiement officiel Forfeo.com…', payOnSite: 'Paiement prévu sur place.',
    giftshopAuthority: 'Les montants, taxes, paiements, remboursements et QR ne sont jamais décidés par le navigateur.', retry: 'Réessayer',
  },
  'en-CA': {
    title: 'Book with Forfeo Reserve',
    subtitle: 'Choose a real slot. Official payments and passes remain managed by Forfeo.com.',
    service: 'Service', schedule: 'Date and time', options: 'Options', review: 'Review', complete: 'Confirmation',
    date: 'Date', time: 'Time', duration: 'Duration', people: 'Guests', noOptions: 'No additional options for this service.',
    availability: 'Availability', policy: 'Policy', next: 'Continue', back: 'Back', check: 'Check this slot',
    reserve: 'Confirm booking', pay: 'Continue to Forfeo.com', waitlist: 'Join waitlist',
    remaining: 'spots remaining', expires: 'The slot is temporarily held.', confirmed: 'Your booking is confirmed.',
    paymentRedirect: 'Redirecting to the official Forfeo.com checkout…', payOnSite: 'Payment is due on site.',
    giftshopAuthority: 'Amounts, taxes, payments, refunds and QR codes are never decided by the browser.', retry: 'Try again',
  },
};

function localDate(days = 1): string {
  const date = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  return date.toISOString().slice(0, 10);
}

function toIso(date: string, time: string): string {
  const parsed = new Date(`${date}T${time}:00`);
  if (!Number.isFinite(parsed.getTime())) throw new Error('invalid_booking_window');
  return parsed.toISOString();
}

function addMinutes(iso: string, minutes: number): string {
  return new Date(Date.parse(iso) + minutes * 60_000).toISOString();
}

function message(code: string, locale: ForfeoReserveUserLocale): string {
  const en = locale === 'en-CA';
  const messages: Record<string, [string, string]> = {
    authentication_required: ['Connectez-vous pour réserver.', 'Sign in to book.'],
    capacity_conflict: ['Ce créneau vient de devenir indisponible.', 'This slot has just become unavailable.'],
    giftshop_checkout_unavailable: ['Le paiement Forfeo.com est temporairement indisponible.', 'Forfeo.com checkout is temporarily unavailable.'],
    policy_deadline_passed: ['La politique ne permet plus cette action.', 'The policy no longer allows this action.'],
    selection_resolution_failed: ['Impossible de vérifier ce créneau.', 'This slot could not be checked.'],
  };
  return (messages[code] || ['Une erreur est survenue. Réessayez.', 'Something went wrong. Please try again.'])[en ? 1 : 0];
}

const ReserveUserExperience: React.FC<Props> = ({
  businessId,
  preferredAccommodationId,
  initialPartySize = 2,
  initialDate,
  initialEndDate,
}) => {
  const [locale, setLocale] = useState<ForfeoReserveUserLocale>(() => currentForfeoReserveUserLocale());
  const copy = COPY[locale];
  const [catalog, setCatalog] = useState<ForfeoReserveCatalogItemWithSource[]>([]);
  const [catalogItemId, setCatalogItemId] = useState('');
  const [date, setDate] = useState(() =>
    initialDate && /^\d{4}-\d{2}-\d{2}$/.test(initialDate) ? initialDate : localDate(),
  );
  const [time, setTime] = useState('18:00');
  const [duration, setDuration] = useState(() => {
    if (initialDate && initialEndDate) {
      const start = Date.parse(`${initialDate}T00:00:00`);
      const end = Date.parse(`${initialEndDate}T00:00:00`);
      if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
        return Math.max(1440, Math.round((end - start) / 60_000));
      }
    }
    return 60;
  });
  const [partySize, setPartySize] = useState(() => Math.max(1, Math.floor(initialPartySize || 2)));
  const [optionSelections, setOptionSelections] = useState<ForfeoReserveUserOptionSelection[]>([]);
  const [resolution, setResolution] = useState<ForfeoReserveUserResolution | null>(null);
  const [step, setStep] = useState<Step>('service');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [hold, setHold] = useState<{ holdId: string; revision: number; expiresAt: string } | null>(null);
  const [bookingId, setBookingId] = useState('');

  const selectedItem = useMemo(
    () => catalog.find((item) => item.id === catalogItemId) || null,
    [catalog, catalogItemId],
  );
  const startsAt = useMemo(() => {
    try { return toIso(date, time); } catch { return ''; }
  }, [date, time]);
  const endsAt = startsAt ? addMinutes(startsAt, duration) : '';

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true); setError('');
      try {
        const items = (await fetchForfeoReserveUserCatalog(
          createForfeoReserveUserCatalogRequest({ businessId, locale }),
        )) as ForfeoReserveCatalogItemWithSource[];
        if (!active) return;
        setCatalog(items);
        const preferredItem = preferredAccommodationId
          ? items.find((item) => item.sourceAccommodationId === preferredAccommodationId) || null
          : null;
        const currentItem = items.find((item) => item.id === catalogItemId) || null;
        const nextItem = preferredItem || currentItem || items[0] || null;
        if (nextItem && nextItem.id !== catalogItemId) {
          setCatalogItemId(nextItem.id);
          const preferredWindowMinutes = initialDate && initialEndDate
            ? Math.max(0, Math.round((Date.parse(`${initialEndDate}T00:00:00`) - Date.parse(`${initialDate}T00:00:00`)) / 60_000))
            : 0;
          setDuration(preferredWindowMinutes > 0 ? preferredWindowMinutes : (nextItem.defaultDurationMinutes || 60));
          setPartySize(Math.max(
            nextItem.minimumPartySize,
            Math.min(initialPartySize || 2, nextItem.maximumPartySize),
          ));
        }
      } catch (cause) {
        if (active) setError(message(cause instanceof Error ? cause.message : '', locale));
      } finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [businessId, locale, preferredAccommodationId, initialPartySize, initialDate, initialEndDate]);

  useEffect(() => {
    if (!selectedItem) return;
    setDuration(selectedItem.defaultDurationMinutes || 60);
    setPartySize((current) => Math.max(selectedItem.minimumPartySize, Math.min(current, selectedItem.maximumPartySize)));
    setOptionSelections([]);
    setResolution(null);
    setHold(null);
  }, [selectedItem?.id]);

  const updateOption = (code: string, quantity: number) => {
    setOptionSelections((current) => {
      const remaining = current.filter((entry) => entry.code !== code);
      return quantity > 0 ? [...remaining, { code, quantity }] : remaining;
    });
  };

  const resolveSelection = async () => {
    if (!selectedItem || !startsAt || !endsAt) return;
    setBusy(true); setError(''); setHold(null);
    try {
      const result = await resolveForfeoReserveUserSelection(
        createForfeoReserveUserResolveRequest({
          businessId,
          catalogItemId: selectedItem.id,
          startsAt,
          endsAt,
          partySize,
          timezone: selectedItem.timezone,
          locale,
          optionSelections,
        }),
      );
      setResolution(result);
      setStep('review');
    } catch (cause) {
      setError(message(cause instanceof Error ? cause.message : '', locale));
    } finally { setBusy(false); }
  };

  const joinWaitlist = async () => {
    if (!selectedItem) return;
    setBusy(true); setError('');
    try {
      await applyForfeoReserveUserCommand(createForfeoReserveUserCommand({
        kind: 'join_waitlist', businessId, catalogItemId: selectedItem.id,
        startsAt, endsAt, partySize, locale,
      }));
      setStep('complete');
    } catch (cause) { setError(message(cause instanceof Error ? cause.message : '', locale)); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!selectedItem || !resolution) return;
    setBusy(true); setError('');
    try {
      const holdResult = await applyForfeoReserveUserCommand(createForfeoReserveUserCommand({
        kind: 'create_hold', businessId, catalogItemId: selectedItem.id,
        startsAt, endsAt, partySize, optionSelections, locale,
      }));
      const holdId = String(holdResult.holdId || '');
      const revision = Number(holdResult.revision || 1);
      const expiresAt = String(holdResult.expiresAt || '');
      if (!holdId) throw new Error('hold_creation_failed');
      setHold({ holdId, revision, expiresAt });

      const mode = resolution.policy.confirmationMode;
      if (mode === 'free_booking' || mode === 'pay_on_site') {
        const confirmed = await applyForfeoReserveUserCommand(createForfeoReserveUserCommand({
          kind: 'confirm_no_checkout', businessId, holdId, expectedRevision: revision, locale,
        }));
        setBookingId(String(confirmed.bookingId || ''));
        setStep('complete');
        return;
      }

      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('reserve_payment', 'success');
      returnUrl.searchParams.set('reserve_hold', holdId);
      const cancelUrl = new URL(window.location.href);
      cancelUrl.searchParams.set('reserve_payment', 'cancelled');
      const checkout = await createForfeoReserveFinancialCheckout({
        holdId,
        expectedRevision: revision,
        successUrl: returnUrl.toString(),
        cancelUrl: cancelUrl.toString(),
      });
      window.location.assign(checkout.checkoutUrl);
    } catch (cause) {
      setError(message(cause instanceof Error ? cause.message : '', locale));
    } finally { setBusy(false); }
  };

  const steps: Array<{ id: Step; label: string }> = [
    { id: 'service', label: copy.service }, { id: 'schedule', label: copy.schedule },
    { id: 'options', label: copy.options }, { id: 'review', label: copy.review },
  ];

  if (loading) return <div className="max-w-5xl mx-auto rounded-3xl bg-white p-12 text-center"><Loader2 className="mx-auto animate-spin text-blue-600" size={36} /></div>;

  return (
    <section className="max-w-5xl mx-auto rounded-3xl border border-slate-200 bg-white shadow-xl overflow-hidden" aria-labelledby="reserve-user-title">
      <header className="bg-white text-slate-900 px-5 py-7 sm:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.22em] text-blue-300"><ShieldCheck size={15} /> Forfeo Reserve</div>
            <h1 id="reserve-user-title" className="mt-2 text-2xl sm:text-3xl font-black tracking-tight">{copy.title}</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-700">{copy.subtitle}</p>
          </div>
          <div className="inline-flex rounded-xl border border-white/15 bg-white/5 p-1" aria-label="Language">
            {(['fr-CA', 'en-CA'] as const).map((value) => (
              <button key={value} type="button" onClick={() => setLocale(value)} className={`rounded-lg px-3 py-2 text-xs font-black ${locale === value ? 'bg-white text-slate-950' : 'text-slate-900'}`} aria-pressed={locale === value}>{value === 'fr-CA' ? 'FR' : 'EN'}</button>
            ))}
          </div>
        </div>
      </header>

      {step !== 'complete' && (
        <nav className="grid grid-cols-2 gap-2 border-b border-slate-100 bg-slate-50 px-4 py-4 sm:grid-cols-4 sm:px-8" aria-label="Booking steps">
          {steps.map((entry, index) => {
            const current = steps.findIndex((candidate) => candidate.id === step);
            return <div key={entry.id} className={`flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-black ${index === current ? 'bg-blue-600 text-white' : index < current ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700'}`}><span className="grid h-6 w-6 place-items-center rounded-full bg-current/10">{index < current ? '✓' : index + 1}</span>{entry.label}</div>;
          })}
        </nav>
      )}

      <div className="p-5 sm:p-8">
        {error && <div role="alert" className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700"><AlertCircle className="mt-0.5 shrink-0" size={18} />{error}</div>}

        {step === 'service' && (
          <div>
            <h2 className="text-xl font-black text-slate-900">{copy.service}</h2>
            {catalog.length === 0 ? <p className="mt-4 rounded-2xl bg-slate-50 p-6 text-slate-700">{locale === 'en-CA' ? 'No Reserve service is currently active.' : 'Aucun service Reserve n’est actuellement actif.'}</p> : <div className="mt-5 grid gap-3 sm:grid-cols-2">{catalog.map((item) => <button key={item.id} type="button" onClick={() => setCatalogItemId(item.id)} className={`min-h-32 rounded-2xl border p-5 text-left transition ${catalogItemId === item.id ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}><div className="font-black text-slate-900">{item.label}</div><div className="mt-2 text-sm text-slate-700">{item.description || item.resourceKind}</div><div className="mt-3 text-xs font-bold text-slate-700">{item.minimumPartySize}–{item.maximumPartySize} {copy.people.toLowerCase()}</div></button>)}</div>}
            <div className="mt-7 flex justify-end"><button type="button" disabled={!selectedItem} onClick={() => setStep('schedule')} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-black text-white disabled:opacity-40">{copy.next}<ArrowRight size={17} /></button></div>
          </div>
        )}

        {step === 'schedule' && selectedItem && (
          <div>
            <h2 className="text-xl font-black text-slate-900">{copy.schedule}</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <label className="text-sm font-bold text-slate-700"><span className="mb-2 flex items-center gap-2"><CalendarDays size={16} />{copy.date}</span><input type="date" min={localDate(0)} value={date} onChange={(event) => setDate(event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-3" /></label>
              <label className="text-sm font-bold text-slate-700"><span className="mb-2 flex items-center gap-2"><Clock3 size={16} />{copy.time}</span><input type="time" value={time} onChange={(event) => setTime(event.target.value)} className="w-full rounded-xl border border-slate-200 px-3 py-3" /></label>
              <label className="text-sm font-bold text-slate-700"><span className="mb-2 block">{copy.duration}</span><select value={duration} onChange={(event) => setDuration(Number(event.target.value))} className="w-full rounded-xl border border-slate-200 px-3 py-3">{[30, 45, 60, 90, 120, 180, 1440].map((minutes) => <option key={minutes} value={minutes}>{minutes < 1440 ? `${minutes} min` : locale === 'en-CA' ? '1 day' : '1 jour'}</option>)}</select></label>
              <label className="text-sm font-bold text-slate-700"><span className="mb-2 flex items-center gap-2"><Users size={16} />{copy.people}</span><input type="number" min={selectedItem.minimumPartySize} max={selectedItem.maximumPartySize} value={partySize} onChange={(event) => setPartySize(Number(event.target.value))} className="w-full rounded-xl border border-slate-200 px-3 py-3" /></label>
            </div>
            <p className="mt-4 text-xs text-slate-700">{selectedItem.timezone}</p>
            <div className="mt-7 flex flex-wrap justify-between gap-3"><button type="button" onClick={() => setStep('service')} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 font-black text-slate-700"><ArrowLeft size={17} />{copy.back}</button><button type="button" onClick={() => setStep('options')} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-black text-white">{copy.next}<ArrowRight size={17} /></button></div>
          </div>
        )}

        {step === 'options' && selectedItem && (
          <div>
            <h2 className="text-xl font-black text-slate-900">{copy.options}</h2>
            {selectedItem.options.length === 0 ? <p className="mt-5 rounded-2xl bg-slate-50 p-6 text-slate-700">{copy.noOptions}</p> : <div className="mt-5 space-y-3">{selectedItem.options.map((option) => { const quantity = optionSelections.find((entry) => entry.code === option.code)?.quantity || 0; return <div key={option.code} className="flex flex-col gap-4 rounded-2xl border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between"><div><div className="font-black text-slate-900">{option.label}</div><div className="mt-1 text-sm text-slate-700">{option.description}</div>{option.fulfillmentKind === 'giftshop' && <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-1 text-[10px] font-black uppercase text-purple-700"><CreditCard size={12} /> Forfeo.com</div>}</div><select aria-label={option.label} value={quantity} onChange={(event) => updateOption(option.code, Number(event.target.value))} className="rounded-xl border border-slate-200 px-4 py-3">{Array.from({ length: option.maximumQuantity + 1 }, (_, value) => <option key={value} value={value}>{value}</option>)}</select></div>; })}</div>}
            <div className="mt-7 flex flex-wrap justify-between gap-3"><button type="button" onClick={() => setStep('schedule')} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 font-black text-slate-700"><ArrowLeft size={17} />{copy.back}</button><button type="button" disabled={busy} onClick={resolveSelection} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-black text-white disabled:opacity-50">{busy ? <Loader2 className="animate-spin" size={17} /> : <RefreshCcw size={17} />}{copy.check}</button></div>
          </div>
        )}

        {step === 'review' && selectedItem && resolution && (
          <div>
            <h2 className="text-xl font-black text-slate-900">{copy.review}</h2>
            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 p-5"><div className="text-xs font-black uppercase tracking-widest text-slate-700">{selectedItem.label}</div><div className="mt-3 space-y-2 text-sm text-slate-700"><p><strong>{copy.date}:</strong> {new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeStyle: 'short' }).format(new Date(startsAt))}</p><p><strong>{copy.people}:</strong> {partySize}</p><p><strong>{copy.availability}:</strong> {resolution.availability.state} — {resolution.availability.remainingCapacity} {copy.remaining}</p></div></div>
              <div className="rounded-2xl border border-slate-200 p-5"><div className="text-xs font-black uppercase tracking-widest text-slate-700">{copy.policy}</div><div className="mt-3 text-lg font-black text-slate-900">{resolution.policy.confirmationMode || '—'}</div>{resolution.policy.confirmationMode === 'pay_on_site' && <p className="mt-2 text-sm text-slate-600">{copy.payOnSite}</p>}<p className="mt-3 flex items-start gap-2 text-xs text-slate-700"><LockKeyhole className="mt-0.5 shrink-0" size={14} />{copy.giftshopAuthority}</p></div>
            </div>
            {hold && <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-bold text-amber-800">{copy.expires} {hold.expiresAt ? new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(hold.expiresAt)) : ''}</div>}
            <div className="mt-7 flex flex-wrap justify-between gap-3"><button type="button" onClick={() => { setStep('options'); setResolution(null); }} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-5 py-3 font-black text-slate-700"><ArrowLeft size={17} />{copy.back}</button>{resolution.availability.state === 'fully_booked' ? <button type="button" disabled={busy} onClick={joinWaitlist} className="rounded-xl bg-blue-600 px-5 py-3 font-black text-white">{copy.waitlist}</button> : <button type="button" disabled={busy || resolution.policy.resolutionState !== 'resolved'} onClick={submit} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 font-black text-white disabled:opacity-50">{busy ? <Loader2 className="animate-spin" size={17} /> : resolution.policy.financialHandoffRequired ? <CreditCard size={17} /> : <CheckCircle2 size={17} />}{resolution.policy.financialHandoffRequired ? copy.pay : copy.reserve}</button>}</div>
          </div>
        )}

        {step === 'complete' && (
          <div className="py-8 text-center"><div className="mx-auto grid h-20 w-20 place-items-center rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 size={42} /></div><h2 className="mt-5 text-3xl font-black text-slate-900">{copy.confirmed}</h2>{bookingId && <p className="mt-3 text-sm font-bold text-slate-700">#{bookingId.slice(0, 8)}</p>}<a href="/mes-reservations" className="mt-7 inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 font-black text-slate-900">{locale === 'en-CA' ? 'My bookings' : 'Mes réservations'}<ArrowRight size={17} /></a></div>
        )}
      </div>
    </section>
  );
};

export default ReserveUserExperience;
